import React, { useState, useEffect } from 'react';
import { trackingService, featureFlagsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  MapPin, 
  Clock, 
  Navigation,
  AlertTriangle,
  RefreshCw,
  Truck,
  Activity
} from 'lucide-react';

const TrackingDashboard = () => {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [driverLocations, setDriverLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('map');

  useEffect(() => {
    checkFeatureAndLoad();
  }, []);

  const checkFeatureAndLoad = async () => {
    try {
      const enabledFeatures = await featureFlagsService.getEnabledFeatures();
      const isEnabled = enabledFeatures.gps_tracking_enabled === true;
      setFeatureEnabled(isEnabled);
      
      if (isEnabled) {
        await loadTrackingData();
      }
    } catch (err) {
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadTrackingData = async () => {
    try {
      const data = await trackingService.getAllDriverLocations();
      setDriverLocations(data.locations || []);
    } catch (err) {
      console.error('Failed to load tracking data:', err);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-yellow-800">Feature Not Enabled</h2>
                  <p className="text-yellow-700 mt-2">
                    GPS Tracking is currently disabled. Contact your administrator to enable this feature.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">GPS Tracking</h1>
            <p className="text-gray-600 mt-1">Real-time driver location tracking</p>
          </div>
          <Button onClick={loadTrackingData} className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Truck className="w-8 h-8 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold">{driverLocations.length}</div>
                  <div className="text-sm text-gray-500">Active Drivers</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Navigation className="w-8 h-8 text-green-600" />
                <div>
                  <div className="text-2xl font-bold">
                    {driverLocations.filter(d => d.speed > 0).length}
                  </div>
                  <div className="text-sm text-gray-500">Moving</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-8 h-8 text-orange-600" />
                <div>
                  <div className="text-2xl font-bold">
                    {driverLocations.filter(d => !d.speed || d.speed === 0).length}
                  </div>
                  <div className="text-sm text-gray-500">Stationary</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Activity className="w-8 h-8 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold">--</div>
                  <div className="text-sm text-gray-500">ELD Connected</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex border-b mb-6">
          {['map', 'list', 'eld'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'eld' ? 'ELD Status' : tab === 'map' ? 'Map View' : 'Driver List'}
            </button>
          ))}
        </div>

        {activeTab === 'map' && (
          <Card>
            <CardContent className="pt-6">
              <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Map integration placeholder</p>
                  <p className="text-sm">Connect a mapping service (Google Maps, Mapbox) to display live locations</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'list' && (
          <Card>
            <CardHeader>
              <CardTitle>Driver Locations</CardTitle>
            </CardHeader>
            <CardContent>
              {driverLocations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No driver locations available. Drivers need to update their location.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Driver</th>
                        <th className="text-left py-3 px-2">Location</th>
                        <th className="text-left py-3 px-2">Speed</th>
                        <th className="text-left py-3 px-2">Last Update</th>
                        <th className="text-left py-3 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driverLocations.map((loc, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <div className="font-medium">{loc.driver_name || loc.driver_id}</div>
                          </td>
                          <td className="py-3 px-2">
                            <div className="text-xs text-gray-500">
                              {loc.latitude?.toFixed(4)}, {loc.longitude?.toFixed(4)}
                            </div>
                          </td>
                          <td className="py-3 px-2">{loc.speed || 0} mph</td>
                          <td className="py-3 px-2 text-xs">{formatTime(loc.recorded_at)}</td>
                          <td className="py-3 px-2">
                            <Badge className={loc.speed > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {loc.speed > 0 ? 'Moving' : 'Stopped'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'eld' && (
          <Card>
            <CardHeader>
              <CardTitle>ELD Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>ELD Integration placeholder</p>
                <p className="text-sm">Connect an ELD provider (KeepTruckin, Samsara) to display HOS data</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TrackingDashboard;
